// Cross-window sync for the Generic CRUD engine's Undock feature (see
// office-portal/design/design_build_instruction__metadata_driven_workspace_platform__vn04.md
// §9-10, scoped down to just this one slice - see CRUD_engine_test_plan.md "Phase 5").
// Generic on purpose (no entity names baked in, per META-05): carries whichever entity/row/tab
// the main generic_crud.tsx window currently has focused, so an undocked detail window can render
// the same reusable ChildDetailPanel in sync.
const CHANNEL_NAME = "eyebright-workspace-context";

export type WorkspaceContext = {
  selectedEntity: string | null;
  focusedRowPk: number | string | null;
  focusedRowLabel: string | null;
  activeChildTab: number;
  version: number;
  sourceWindowId: string;
  timestamp: string;
};

// A window.open()'d detail window subscribes only after its own React tree mounts, which is
// always later than the instant the opener calls window.open() - so a plain "publish on undock"
// races the new window's subscription and the very first message is lost (BroadcastChannel never
// queues messages for listeners that attach late). REQUEST lets a newly-opened window ask "what's
// the current context?" and any window holding one replies immediately.
type ChannelMessage = { kind: "context"; ctx: WorkspaceContext } | { kind: "request" };

// One id per window/tab (not per component instance), so a window that ignores its own
// broadcasts still recognises itself after a remount (e.g. Fast Refresh in dev).
export const WORKSPACE_WINDOW_ID: string =
  typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `win-${Date.now()}-${Math.random().toString(36).slice(2)}`;

function isBroadcastChannelSupported(): boolean {
  return typeof window !== "undefined" && "BroadcastChannel" in window;
}

let channel: BroadcastChannel | null = null;
let lastPublished: WorkspaceContext | null = null;

function getChannel(): BroadcastChannel | null {
  if (!isBroadcastChannelSupported()) return null;
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL_NAME);
    // Auto-responder: whichever window last published a context (normally just the main
    // generic_crud.tsx window) replies with it whenever anyone asks. A no-op in every window that
    // has never published (lastPublished stays null), so this is safe to register everywhere.
    channel.addEventListener("message", (event: MessageEvent<ChannelMessage>) => {
      if (event.data?.kind === "request" && lastPublished) {
        channel!.postMessage({ kind: "context", ctx: lastPublished } satisfies ChannelMessage);
      }
    });
  }
  return channel;
}

let versionCounter = 0;

// REQ-SYNC-004/014: publish the current workspace context. Callers pass everything except
// version/sourceWindowId/timestamp, which this function always stamps itself so publishers can't
// drift out of sync with each other on those fields.
export function publishWorkspaceContext(ctx: Omit<WorkspaceContext, "version" | "sourceWindowId" | "timestamp">): void {
  const ch = getChannel();
  if (!ch) return;
  versionCounter += 1;
  const message: WorkspaceContext = {
    ...ctx,
    version: versionCounter,
    sourceWindowId: WORKSPACE_WINDOW_ID,
    timestamp: new Date().toISOString(),
  };
  lastPublished = message;
  ch.postMessage({ kind: "context", ctx: message } satisfies ChannelMessage);
}

// Called by a newly-opened detail window right after it subscribes, so it doesn't have to wait
// for the next real focus/selection change in the main window to see what's currently selected.
export function requestWorkspaceContext(): void {
  const ch = getChannel();
  if (!ch) return;
  ch.postMessage({ kind: "request" } satisfies ChannelMessage);
}

// REQ-SYNC-005/015: subscribers never see their own window's messages, and never see a message
// older than the last one they've already processed (guards against out-of-order delivery).
export function subscribeWorkspaceContext(handler: (ctx: WorkspaceContext) => void): () => void {
  const ch = getChannel();
  if (!ch) return () => {};
  let lastSeenVersion = -1;
  const listener = (event: MessageEvent<ChannelMessage>) => {
    if (event.data?.kind !== "context") return;
    const ctx = event.data.ctx;
    if (!ctx || ctx.sourceWindowId === WORKSPACE_WINDOW_ID) return;
    if (ctx.version <= lastSeenVersion) return;
    lastSeenVersion = ctx.version;
    handler(ctx);
  };
  ch.addEventListener("message", listener);
  return () => ch.removeEventListener("message", listener);
}

export function isWorkspaceSyncSupported(): boolean {
  return isBroadcastChannelSupported();
}
